
import './polyfills/closest';

import getCountryCode from './i18n/getCountryCode';
import { phoneNumberMask } from './validation/phoneNumber';
import validateForm from './validation/validateForm';
import { formFieldValue, formSelectorValues, formFieldChecked } from './selectors/formFieldValue';
import VMasker from 'vanilla-masker';
import submitForm from './form/submitForm';
import removeClass from './selectors/removeClass';
import addClass from './selectors/addClass';
import forEachElement from './selectors/forEachElement';
import isProject from './selectors/isProject';
import ajaxUrl from './form/ajaxUrl';
import formBrand from './form/formBrand';
import dayjs from 'dayjs';
import Cookies from 'universal-cookie';
import getLandingPage from '../leadform/models/metadata/get_landing_page';
import getVisitorId from '../leadform/models/metadata/get_visitor_id';
import navbarHeight from './selectors/navbarHeight';
import scrollToElement from './animations/scrollTo';
import timezone from '../date/timezone';
import getFieldValueByName from '../leadform/models/form/get_field_values_by_name';
import dispatchCustomEvent from './events/dispatchCustomEvent';
import getActivePanel from '../leadform/models/form/get_active_panel';
import {
  additionalFormFieldsAsHash,
  additionalFormFieldsAsString,
  getAdditionalFormFields
} from './form/additionalFormFields';
import { pushLeadCreated } from '../templates/account_creation/sendToGtm';

/**
 This file contains all the js required for form validation and submission.
 It also adds the fields required for vt-contact and Looker, like the leads Page
 field and other params required for lead attribution.
*/

var grade = 0;
const cookies = new Cookies();

/**
 * Converts lead attribution fields into a query string to send as a single param
 *
 * @param {element} form Form currently being validated
 * @returns {string}
 */
const attributionField = function (form) {
  const fieldNames = ['vtmedium', 'vtsource', 'vtcampaign', 'vtplacement', 'leadform', 'abtest'];
  // Impact radius refers potential leads, which marketing tracks
  const irclickid = cookies.get('irclickid');

  const attributionFields = fieldNames
    .filter(fieldName => formFieldValue(form, fieldName))
    .map(fieldName => `${fieldName}=${formFieldValue(form, fieldName)}`);

  if (irclickid) {
    attributionFields.push(`;;irclickid=${irclickid}`);
  }
  return attributionFields.join('&');
};

/**
 * Determines thank you page user gets redirected to upon successful form submission
 *
 * @param {element} form Form currently being validated
 * @return {string} thank you page url (or '' if user shouldn't get redirected)
 */
const determineThankYouPage = function (form) {
  let thankYouPage = '';
  const redirectUrl = formFieldValue(form, 'redirecturl');

  if (redirectUrl && formBrand(form) === 'vp') {
    thankYouPage = `https://${process.env.vpWwwHost}${redirectUrl}`;
  } else if (isProject('vt_package_experience_hagrid')) {
    thankYouPage = determineBundleRedirect(form, redirectUrl);
  } else if (redirectUrl) {
    thankYouPage = `https://${process.env.wwwHost}${redirectUrl}`;
  }

  return thankYouPage;
};

/**
 * Determines which bundle page user gets redirected to per form responses
 * See vt_package_experience_hagrid & SGS-1010
*/
const determineBundleRedirect = function (form, redirectUrl) {
  const formSpells = getAdditionalFormFields(form, 'spells');
  const howSoon = formSpells['how-soon'];
  const whoNeedsTutor = formSpells['who-needs-tutor'];
  let subject = redirectUrl.match(/(subject=)(.*?)(&|$)/)[2];

  if (whoNeedsTutor === 'My child' || howSoon === 'Right away') {
    return `https://${process.env.wwwHost}${redirectUrl}`;
  } else if(subject) {
    return `https://${process.env.wwwHost}/en/${subject}-packages`;
  } else {
    return `https://${process.env.wwwHost}/en/all-packages`;
  }
};

/**
 * Adds masking to phone number input field
 */
const maskPhoneNumberInput = function () {
  const mask = phoneNumberMask(getCountryCode());
  const input = document.querySelector('input[name="phone_number"]');
  if (mask && input) VMasker(input).maskPattern(mask);
};

const setTouHref = function () {
  const touLink = document.querySelector('a.form__field--link');
  if (touLink) {
    const placeholderHref = touLink.href;
    touLink.href = placeholderHref.replace('%wwwHost%', process.env.wwwHost);
  }
};

/**
 * Creates the leads Page field from the conversion page url and params we want
 * to pass to vt-contact (including query string on conversion page, as well as
 * attribution fields from hidden input fields).
 *
 * @param {element} form Form currently being validated
 * @param {string} conversionPage Url that lead converted on
 * @returns {string}
 */
const pageField = function (form, conversionPage) {
  const attrField = attributionField(form);

  if (!attrField) {
    // strip question mark if at the end of URL.
    // vtwa /leads admin page crashes if we send this URL ending in a question mark.
    return conversionPage.replace(/\?$/, '');
  }

  // determine if we have a query string on conversion page
  let queryStrSeparator = '?';
  const urlSplitQueryString = conversionPage.split(queryStrSeparator);
  if (urlSplitQueryString.length > 1) {
    // conversionPage has querystring or ends with `?`
    queryStrSeparator = urlSplitQueryString[1] !== '' ? '&' : '';
  }
  return conversionPage + queryStrSeparator + attrField;
};

const determineConversionPage = function (form) {
  // If form is served as an iFrame, the conversion page comes from document.referrer;
  // otherwise, the current url where the form was submitted
  const iFrameLeadform = form.closest('.vt-leadform-wizard--iframe');
  const conversionPage = iFrameLeadform ? document.referrer : window.location.href;
  return conversionPage || '';
};

/**
 * Sets up the form validation and Ajax form submit
 *
 * @param {string} [leadformWrapperSelector = '.js-leadform'] Outermost wrapper class of leadform
 */
export const setUpFormValidation = function (leadformWrapperSelector = '.js-leadform') {
  maskPhoneNumberInput();
  setTouHref();

  // Initialize form validation on the registration form.
  // It has the name attribute "registration"
  forEachElement(`${leadformWrapperSelector} .form--lead`, form => {
    const conversionPage = determineConversionPage(form);

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      const currentPanel = getActivePanel(form);
      if (validateForm(form)) {
        // allow submit event to be cancelled by listeners
        const submitEv = dispatchCustomEvent(form, 'mslf.beforeSubmit', { form, currentPanel });
        if(!submitEv.defaultPrevented) submitHandler(form, conversionPage);
      }
    });

    // also validate form when user updates international phone_number_ui country
    form.addEventListener('countrychange', function (event) {
      event.preventDefault();
      validateForm(form);
    });

    const revalidate = function (event) {
      const { target } = event;
      if (target.classList.contains('js-input-validated')) {
        if (target.closest('.lead-constructor__panel').classList.contains('active')) {
          validateForm(form);
        }
      }
    };

    form.addEventListener('keyup', revalidate);
    form.addEventListener('change', revalidate);
  });
};

/**
 * Callback for handling the actual submit when the form is validated. Submits
 * the form via Ajax, and redirects user to appropriate thank you page.
 *
 * @param {element} form Form currently being validated
 * @param {string} conversionPage Url that lead converted on
 */
const submitHandler = function (form, conversionPage) {
  // if on a wizard form, prevent the form from submitting early
  // and instead trigger a click on the next button
  let nextButton = form.querySelector('.active .js-lead-constructor__next');

  if (nextButton) {
    nextButton.click();
  } else {
    var formDataAsJSON;
    if (isProject('vt_virtual_learning_hagrid')) {
      formDataAsJSON = getReachFormData(form);
    }
    else {
      formDataAsJSON = getFormData(form, conversionPage);
    }
    const thankYouPage = determineThankYouPage(form);

    postForm(formDataAsJSON, thankYouPage, form);
  }
};

// Get data from form
const getFormData = function (form, conversionPage) {
  const tutoringTypes = [...document.getElementsByName('tutoring-type')]
    .filter(el => el.checked)
    .map(el => el.value)
    .join();

  const additionalFormFields = getAdditionalFormFields(form);

  const formData = {
    data: {
      name: {
        full_name: formFieldValue(form, 'full_name')
      },
      contact_details: {
        email: formFieldValue(form, 'email'),
        phone: (formFieldValue(form, 'phone_number') || '').replace(/[^\d+]/g, ''),
      },
      location: {
        country_code: getCountryCode(),
        zip_code: getZipCode(form)
      },
      tutoring_preferences: {
        types: tutoringTypes,
        subjects: formFieldValue(form, 'subjects')
      },
      referer: formFieldValue(form, 'referer'),
      page: pageField(form, conversionPage),
      message: additionalFormFieldsAsString(additionalFormFields),
      landing_page: getLandingPage(form),
      referred_by_url: getReferredByUrl(form),
      visitor_id: getVisitorId(form)
    }
  };
  Object.assign(formData['data'], additionalFormFieldsAsHash(additionalFormFields));
  Object.assign(formData['data'], optionalDataFields(form));
  Object.assign(formData['data']['contact_details'], optionalContactDetailFields(form));
  Object.assign(formData['data'], abTestFieldsAsHash(form));
  Object.assign(formData['data'], experienceAsHash(form));
  Object.assign(formData['data'], orgProgramAsHash(form));

  return JSON.stringify(formData);
};

const getReachFormData = function (form) {
  grade = getGradeFormValue(form);
  const additionalFormFields = getAdditionalFormFields(form);
  const formData = {
    data: {
      full_name: formFieldValue(form, 'full_name'),
      email: formFieldValue(form, 'email_required'),
      phone: formFieldValue(form, 'phone_number').replace(/[^\d]/g, ''),
      landing_page: getLandingPage(form),
      visitor_id: getVisitorId(form),
      grade: getGradeFormValue(form),
      password: formFieldValue(form, 'password'),
      message: additionalFormFieldsAsString(additionalFormFields),
      referrer: getRefererCookie()
    }
  };

  return JSON.stringify(formData);
};

// Returns '00000' for vt_school_at_home_hagrid as it does not have a zip panel
// Returns the field value for other leadforms
export const getZipCode = function (form) {
  if (isProject('vt_school_at_home_hagrid')) {
    return '00000';
  }
  return formFieldValue(form, 'zip_code');
};

// referred_by_url should be set to the original referer **only for non-VT forms**
// VT forms populate the referer field with the original referer
const getReferredByUrl = function (form) {
  const referred_by_url = formFieldValue(form, 'referred_by_url');
  if (referred_by_url) return referred_by_url;
};

// Builds a hash of the optional data fields to be added to the hash
export const optionalDataFields = function (form) {
  let result = {};

  // Callback scheduling
  const timeToCall = formFieldValue(form, 'time_to_call');
  if (timeToCall != null) {
    result['callback_time'] = timeToCall;
  }
  return result;
};

// Builds a hash of optional contact detail fields
export const optionalContactDetailFields = function (form) {
  let result = {};

  // Consent to being called
  const ttc = formFieldValue(form, 'dialer_consent_accepted');
  if (ttc != null) {
    result['dialer_consent_accepted'] = formFieldChecked(form, 'dialer_consent_accepted');
  }
  return result;
};

function getGradeFormValue(form) {
  const elementary = formFieldValue(form, 'student-grade-elementary');
  if (elementary) {
    return gradeValues[elementary];
  }
  const middle = formFieldValue(form, 'student-grade-middle');
  if (middle) {
    return gradeValues[middle];
  }
  const high = formFieldValue(form, 'student-grade-high');
  if (high) {
    return gradeValues[high];
  }
}

function getRefererCookie() {
  const domain = window.location.hostname;
  return cookies.get('referer', { domain: domain, path: '/' });
}

/**
 * Experiment data passed for better sales/BI tracking
 * This function creates a hash with the abtest experiment name and variant if present
 *
 * @param {element} form Form currently being validated
 * @returns {hash} Hash of abtest values
 */
const abTestFieldsAsHash = function (form) {
  let hash = {};
  const ab_test_split = formFieldValue(form, 'abtest', '').split('-');
  if (ab_test_split.length == 2) {
    hash['ab_test_name'] = ab_test_split[0];
    hash['ab_test_variant'] = ab_test_split[1];
  }
  return hash;
};

const orgProgramAsHash = function (form) {
  const redirectUrl = formFieldValue(form, 'redirecturl');
  let org = redirectUrl.match(/(organization=)(.*?)(&|$)/);

  const project = orgProjectCheck(form, org);
  if (project) {
    return project;
  } else if (org != null) {
    if (org[2] == 'amazon-learning-differences') {
      return { program: 'Partners-learning-differences', organization: org[2] };
    }
    return { program: 'Partnership', organization: org[2] };
  } else {
    const landingPage = getLandingPage(form);
    const lp_hash = programLandingPage(landingPage);
    return ( lp_hash ? lp_hash : {} );
  }
};

const orgProjectCheck = function (form, org) {
  const company = formFieldValue(form, 'company_name');

  if (isProject('vt_learning_pods_hagrid')) {
    return { program: 'Learning Pod' };
  }
  if (isProject('vt_partnerships_hagrid') || isProject('vt_school_solutions_hagrid')) {
    return { program: 'B2B', organization: company };
  }
  if (isProject('bright_horizons_hagrid')) {
    return { program: 'Partnership', organization: company };
  }
  if (isProject('vt_partners_intl_hagrid')) {
    return { program: 'Partners-international', organization: org[2] };
  }
  if (isProject('vt_partners_promo_hagrid')) {
    const conversionPage = determineConversionPage(form);
    const query = promoCodeQuery(conversionPage, org);
    return ( query ? query : { program: 'Promotion', 'lead_form.promo_code': 'invalid' } );

  }
};

const promoCodeQuery = function (conversionPage) {
  if (conversionPage != '' && conversionPage.includes('promo_code=')) {
    const promoQuery = conversionPage.match(/(promo_code=)(.*?)(&|$)/);
    return { program: 'Promotion', 'lead_form.promo_code': promoQuery[2] };
  }
  return false;
};

const programLandingPage = function (landingPage) {
  if (landingPage.includes('/brighthorizons')) {
    return { program: 'Partnership', organization: 'Bright Horizons' };
  } else if (landingPage.includes('/partnerships')) {
    return { program: 'B2B', organization: 'B2B' };
  } else if (landingPage.includes('/school-solutions')) {
    return { program: 'B2B', organization: 'school-solutions' };
  } else if (landingPage.includes('/promos/')) {
    const org = landingPage.match(/(promos\/)(.+?)(\?|&|$)/);
    return { program: 'Promotion', 'lead_form.promo_code': 'invalid' };
  } else if (landingPage.includes('/partners/')) {
    const org = landingPage.match(/(partners\/)(.+?)(\?|&|$)/);
    return {program: 'Partnership', organization: org[2] };
  } else {
    return false;
  }
};

/**
 * Experience data passed for better lead siloing
 * This function creates a hash with the experience name if present
 *
 * @param {element} form Form currently being validated
 * @returns {hash} Hash of experience value
 */
const experienceAsHash = function (form) {
  const howSoon = getFieldValueByName(form, 'how-soon');
  const whoNeedsTutor = getFieldValueByName(form, 'who-needs-tutor');
  let hash = {};

  if (isProject('vt_package_experience_hagrid')) {
    if (whoNeedsTutor === 'My child' || howSoon === 'Right away') {
      hash['experience'] = 'default';
    } else {
      hash['experience'] = 'package_experience';
    }
  } else if (isProject('vt_adaptive_hagrid')) {
    if (howSoon === 'Right away') {
      hash['experience'] = 'default';
    } else {
      hash['experience'] = 'email_experience';
    }
  }
  return hash;
};

/**
 * Helper function to insert a new hidden input in a form
 *
 * @param {string} name     Name of input
 * @param {string} value    Value of input
 * @param {Element} wrapper Parent element
 */
const insertHiddenField = function (name, value, wrapper) {
  const input = document.createElement('input');
  input.setAttribute('type', 'hidden');
  input.setAttribute('name', name);
  input.setAttribute('value', value);
  wrapper.appendChild(input);
};

/**
 * Helper function for creating querystring params for redirect
 * based on response from vt-contact
 *
 * @param {element} form Form being validated
 * @param {string} response Response from post message
 * @param {*} data An object containing submission data
 */
// eslint-disable-next-line complexity
const createRedirectQueryStrings = (form, response, data) => {
  const unixTimestamp = getUnixTimeToCall(form);

  const timeZone = timezone.name();

  const queryString = [
    `correlation_id=${encodeURIComponent(response.correlationId)}`,
    `phone_number=${encodeURIComponent(response.phoneNumber)}`,
    `time_zone=${encodeURIComponent(timeZone)}`
  ];

  if (unixTimestamp != null) {
    queryString.push(`confirm=${encodeURIComponent(unixTimestamp)}`);
  }

  if (isProject('vt_learning_pods_hagrid')) {
    queryString.push(`contact_uuid=${encodeURIComponent(response.id)}`);
  }

  const touHagrid = isProject('vt_languages_tutoring_tou_hagrid') ||
                    isProject('ppc_locale_only_tou_hagrid');
  if (touHagrid && data) {
    const parsedData = JSON.parse(data).data;
    const contactDetails = parsedData && parsedData.contact_details;
    const dialerConsentAccepted = contactDetails && contactDetails.dialer_consent_accepted;

    if (dialerConsentAccepted) {
      queryString.push(`dialer_consent_accepted=${encodeURIComponent(!!dialerConsentAccepted)}`);
    }
  }

  return queryString.join('&');
};

/**
 * Helper function for getting the unix timestamp of callback time
 *
 * @param {element} form Form being validated
 */
const getUnixTimeToCall = (form) => {
  const callbackElement = form.querySelector('[name="time_to_call"]');
  if (callbackElement == null) {
    return;
  }
  const callbackTime = callbackElement.value;
  const callbackObject = dayjs(callbackTime);
  if (callbackObject.isValid() == false) {
    return;
  }
  const callbackUnix = callbackObject.unix();

  return callbackUnix;
};

const fbPixelCode = function () {
  /* Facebook Pixel Code */
  let fbq;
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ?
        n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
    n.queue = []; t = b.createElement(e); t.async = !0;
    t.src = v; s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  }(window, document, 'script',
    'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', `${process.env.fbqPixelId}`);
  fbq('track', 'Lead');
};

// Post form to vt-contact or vp-endpoint
const postForm = function (data, thankYouPage, form) {
  const formTou = document.querySelector('.form--tou');

  submitForm(
    ajaxUrl(form),
    data,
    'POST',
    grade
  ).then(
    response => response.json()
    /*eslint-disable complexity*/
  ).then(response => {
    forEachElement('.progress-wrapper', el => el.style.display = 'none');
    if (thankYouPage && thankYouPage.length) {
      // Allow redirection to be cancelled or modified
      const evRedirect = dispatchCustomEvent(form, 'mslf.beforeRedirect', { form, thankYouPage });
      if(!evRedirect.defaultPrevented) {
        thankYouPage = evRedirect.detail.thankYouPage;

        const queryString = createRedirectQueryStrings(form, response, data);
        if (thankYouPage.indexOf('?') == -1) {
          window.top.location.href = `${thankYouPage}?${queryString}`;
        } else {
          window.top.location.href = `${thankYouPage}&${queryString}`;
        }
      }
    } else if (isProject(
      'utgalleries_gallery_hagrid',
      'us_news_hagrid',
      'utgalleries_bio_hagrid'
    ) || formBrand(form) === 'vp') {
      insertHiddenField('correlationId', response.correlationId, formTou);
      insertHiddenField('hidden_phone', response.phoneNumber, formTou);
      removeClass('.lead-constructor__panel.active', 'active');
      addClass('.lead-constructor__panel--thank-you-form', 'active');

    } else if (isProject('vt_summer_camp_hagrid')) {
      removeClass('.lead-constructor__panel.active', 'active');
      addClass('.lead-constructor__panel--thank-you-panel-link', 'active');

    } else if (isProject('first_tutors_hagrid')) {
      removeClass('.lead-constructor__panel.active', 'active');
      addClass('.lead-constructor__panel--thank-you-panel', 'active');

    } else if (isProject('vt_auth_tutoring_hagrid')) {
      pushLeadCreated(form, { lead_type: '1on1_tutoring' });
      removeClass('.lead-constructor__panel.active', 'active');
      addClass('.lead-constructor__panel--thank-you-panel-link', 'active');

    } else if (isProject('vt_vsx_vt2_hagrid')) {
      removeClass('.lead-constructor__panel.active', 'active');
      addClass('.lead-constructor__panel--thank-you-panel-link', 'active');

    } else if (isProject('vt_international_spanish_hagrid')) {
      removeClass('.lead-constructor__panel.active', 'active');
      addClass('.lead-constructor__panel--thank-you-panel', 'active');
      fbPixelCode();

    } else if (isProject('vt_learning_coordinator_vashon')) {
      const callbackTimeElement = document.querySelector(
        '.lead-constructor__panel--thank-you-panel .js-object-consultation-time'
      );

      const callbackTime = JSON.parse(data).data.callback_time;
      if (callbackTime) {
        const shortTimezoneName =
          new Date().toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];
        callbackTimeElement.textContent =
          `${dayjs(callbackTime).format('ddd, MMM D, YYYY, h:mm A')} (${shortTimezoneName})`;
      }

      removeClass('.lead-constructor__panel.active', 'active');
      addClass('.lead-constructor__panel--thank-you-panel', 'active');
      scrollToElement(document.querySelector('[data-id=vashon-thank-you-panel]'), -navbarHeight());
    }
    else if (isProject('vt_virtual_learning_hagrid')) {
      removeClass('.lead-constructor__panel.active', 'active');
      addClass('.lead-constructor__panel--thank-you-form', 'active');
      const redirectUrl = response.destination;
      forEachElement('.js-lead-constructor__next', button => {
        button.addEventListener('click', (event) => {
          window.goog_report_conversion(redirectUrl);
        });
      });
      cookies.set('vt_authentication_token', response.token, {
        path: '/'
      });
      cookies.set('vt_virtual_destination', response.destination, {
        path: '/'
      });
      fbPixelCode();
    }

    else {
      forEachElement('.lead-constructor__panel', el => el.innerHTML =
        `<div class="lead-constructor__panel lead-constructor__panel--thank-you-panel active"
             data-id="thank-you-panel">
          <div class="lead-constructor__content">
            <div class="lead-constructor__question-block">
              <h1 class="lead-constructor__heading edge-heading">Thanks!
                You'll be called typically within 24 hours by the tutor or company you contacted.
              </h1>
            </div>
          </div>
        </div>`
      );
    }

  }).catch(function (error) {
    forEachElement('.contact-form__info', el => el.innerHTML =
      'Your request was not sent.<br>Please try again.'
    );
    if (window.newrelic) {
      window.newrelic.noticeError(error, {
        api_endpoint: ajaxUrl(form)
      });
    }
  });
  /*eslint-enable complexity*/
};

const validateType = function () {
  document.getElementById('tutoring-type-2').checked = false;
};

const validateNone = function () {
  return [
    document.getElementById('tutoring-type-0'),
    document.getElementById('tutoring-type-1')
  ].forEach(option => option.checked = false);
};

const validateMultiSelect = function () {
  [...document.getElementsByName('tutoring-type')].forEach((option, idx) => {
    if (idx < 2) {
      option.addEventListener('change', validateType);
    } else {
      option.addEventListener('change', validateNone);
    }
  });
};

module.exports = {
  setUpFormValidation: setUpFormValidation,
  validateMultiSelect: validateMultiSelect,
  getZipCode,
  optionalDataFields,
  optionalContactDetailFields,
  getUnixTimeToCall,
  createRedirectQueryStrings,
  submitHandler,
  validateForm,
  determineConversionPage,
  setTouHref,
  orgProgramAsHash,
  maskPhoneNumberInput,
  programLandingPage,
  experienceAsHash,
  promoCodeQuery
};

const gradeValues = {
  'Kindergarten': 0,
  '1st Grade': 1,
  '2nd Grade': 2,
  '3rd Grade': 3,
  '4th Grade': 4,
  '5th Grade': 5,
  '6th Grade': 6,
  '7th Grade': 7,
  '8th Grade': 8,
  '9th Grade': 9,
  '10th Grade': 10,
  '11th Grade': 11,
  '12th Grade': 12
};



// WEBPACK FOOTER //
// ./client/app/scripts/principles/leadform-validation.js